<template>
    <div class="pb" :class="'pb-' + variant" v-if="is_admin">
        <template v-if="playback">
            <button type="button" class="pb-btn pb-icon" :title="slides && slides.page > 0 ? 'Diapositiva anterior' : 'Volver a la actividad anterior'" :disabled="!has_previous || busy" @click="send_rpc('PREVIOUS_STAGE_QUESTION')"><i class="bi bi-skip-start-fill"></i></button>
            <span v-if="slides" class="pb-slides" title="Diapositiva en pantalla"><i class="bi bi-easel"></i>{{ slides.page + 1 }} / {{ slides.pages }}</span>
            <button v-if="can_resume" type="button" class="pb-btn pb-primary" @click="send_rpc('RESUME_STAGE_PLAYBACK')"><i class="bi bi-play-fill"></i>Reanudar</button>
            <button v-else type="button" class="pb-btn" title="Pausar el avance automático" :disabled="busy" @click="send_rpc('PAUSE_STAGE_PLAYBACK')"><i class="bi bi-pause-fill"></i>Pausar</button>
            <!-- Not while held at a stop: the server would skip ahead and
                 leave that stop's pending "Reanudar" dangling. -->
            <button v-if="!at_stop" type="button" class="pb-btn" :class="{ 'pb-primary': !can_resume }" :disabled="(busy && !is_interlude) || counting_down" @click="send_rpc('NEXT_STAGE_QUESTION')">{{ next_label }}<i class="bi bi-skip-end-fill"></i></button>
            <template v-if="confirm_stop">
                <span class="pb-confirm">¿Terminar la etapa?</span>
                <button type="button" class="pb-btn pb-danger" @click="stop_stage">Sí, terminar</button>
                <button type="button" class="pb-btn" @click="confirm_stop = false">No</button>
            </template>
            <button v-else type="button" class="pb-btn pb-icon" title="Terminar la etapa ahora (todos vuelven a la sala de espera)" @click="confirm_stop = true"><i class="bi bi-stop-fill"></i></button>
        </template>
        <template v-else>
            <button v-if="can_resume" type="button" class="pb-btn pb-primary" @click="send_rpc('RESUME_STAGE_PLAYBACK')"><i class="bi bi-play-fill"></i>Reanudar</button>
            <!-- Launched by hand, outside a stage: same page turning, through
                 the remote's RPC (past the last slide it ends the activity). -->
            <template v-if="slides">
                <button type="button" class="pb-btn pb-icon" title="Diapositiva anterior" :disabled="slides.page <= 0" @click="send_rpc('PRESENTER_STEP', { direction: -1 })"><i class="bi bi-chevron-left"></i></button>
                <span class="pb-slides"><i class="bi bi-easel"></i>{{ slides.page + 1 }} / {{ slides.pages }}</span>
                <button type="button" class="pb-btn pb-icon" title="Diapositiva siguiente" :disabled="slides.page >= slides.pages - 1" @click="send_rpc('PRESENTER_STEP', { direction: 1 })"><i class="bi bi-chevron-right"></i></button>
            </template>
            <button v-if="manual_activity_live" type="button" class="pb-btn pb-primary" @click="send_rpc('END_ACTIVITY', game.current_question)"><i class="bi bi-flag-fill"></i>Finalizar actividad</button>
            <button v-if="can_return_to_lobby && is_live" type="button" class="pb-btn" :class="{ 'pb-primary': !manual_activity_live && !can_resume }" title="Todos los equipos vuelven a la sala de espera" @click="send_rpc('RETURN_TO_LOBBY')"><i class="bi bi-house"></i>Volver a la sala de espera</button>
        </template>
    </div>
</template>

<script>
import playbackState from '@/components/admin/playback-state'

// Transport buttons for whatever is live in the room: prev / pause-resume /
// next / stop while a stage plays, or the way out of a manual launch. Same
// component (and same rules, see playback-state.js) in the LiveDock and in
// a stage's rundown header.
export default {
    name: 'PlaybackControls',
    mixins: [playbackState],
    props: {
        // 'dark' on the LiveDock, 'light' on page surfaces.
        variant: { type: String, default: 'light' },
    },
    data(){
        return { confirm_stop: false };
    },
    methods: {
        stop_stage(){
            this.confirm_stop = false;
            this.send_rpc('STOP_STAGE_PLAYBACK');
        }
    }
}
</script>

<style lang="scss" scoped>
.pb{ display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.pb-btn{
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;
    i{ font-size: 1rem; }
    &:disabled{ opacity: 0.38; }
}
.pb-icon{ padding: 0 9px; }
.pb-confirm{ font-weight: 600; margin: 0 4px 0 8px; }
.pb-slides{
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 6px;
    font-weight: 700;
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

/* Dark: on the LiveDock */
.pb-dark{
    .pb-btn{
        border: 1px solid rgba(255, 255, 255, 0.2);
        background: rgba(255, 255, 255, 0.06);
        color: #fff;
        &:hover:not(:disabled){ background: rgba(255, 255, 255, 0.14); }
    }
    .pb-primary{
        background: #fff;
        color: var(--a-ink);
        border-color: #fff;
        &:hover:not(:disabled){ background: #e9e9e9; }
    }
}

/* Light: on page surfaces */
.pb-light{
    .pb-btn{
        border: 1px solid var(--a-line-strong);
        background: var(--a-surface);
        color: var(--a-text);
        &:hover:not(:disabled){ background: var(--a-surface-2); border-color: var(--a-text-3); }
    }
    .pb-primary{
        background: var(--a-ink);
        border-color: var(--a-ink);
        color: #fff;
        &:hover:not(:disabled){ background: var(--a-ink-2); border-color: var(--a-ink-2); }
    }
}

.pb-danger, .pb-dark .pb-danger, .pb-light .pb-danger{
    background: var(--a-live);
    border-color: var(--a-live);
    color: #fff;
    &:hover:not(:disabled){ background: #c93a3f; border-color: #c93a3f; }
}
</style>
